/**
 * NOXN Theme provider.
 *
 * Applies `data-theme="light|dark"` to a scoped wrapper element that wraps
 * only the signed-in area (dashboard + login + settings). The landing page
 * and wizard remain permanently dark per product decision.
 *
 * Precedence (highest first):
 *   1. Signed-in user's `theme_preference` (server-persisted).
 *   2. localStorage `noxn_theme`.
 *   3. Default: `light`.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/lib/auth";
const STORAGE_KEY = "noxn_theme";
const DEFAULT_THEME = "light";

const ThemeCtx = createContext(null);

const readInitial = () => {
    try {
        const v = localStorage.getItem(STORAGE_KEY);
        if (v === "light" || v === "dark") return v;
    } catch (_) { /* ignore */ }
    return DEFAULT_THEME;
};

export const ThemeProvider = ({ children }) => {
    const { user, updateTheme } = useAuth();
    const [theme, setThemeState] = useState(readInitial);

    // Follow the signed-in user's server-side preference when it appears.
    useEffect(() => {
        if (user?.theme_preference && (user.theme_preference === "light" || user.theme_preference === "dark")) {
            setThemeState(user.theme_preference);
            try { localStorage.setItem(STORAGE_KEY, user.theme_preference); } catch (_) { /* ignore */ }
        }
    }, [user?.theme_preference]);

    const setTheme = useCallback(async (next) => {
        if (next !== "light" && next !== "dark") return;
        setThemeState(next);
        try { localStorage.setItem(STORAGE_KEY, next); } catch (_) { /* ignore */ }
        if (user) await updateTheme(next);
    }, [user, updateTheme]);

    const value = useMemo(() => ({ theme, setTheme }), [theme, setTheme]);
    return <ThemeCtx.Provider value={value}>{children}</ThemeCtx.Provider>;
};

export const useTheme = () => {
    const ctx = useContext(ThemeCtx);
    if (!ctx) throw new Error("useTheme must be used inside ThemeProvider");
    return ctx;
};

/**
 * Convenience wrapper: wrap signed-in pages with <ThemedApp>...</ThemedApp>.
 * The `data-theme` attribute unlocks the light-mode CSS overrides in index.css.
 */
export const ThemedApp = ({ children, className = "" }) => {
    const { theme } = useTheme();
    // Mirror the theme onto <body> so portaled UI (dialogs, toasts, dropdowns)
    // rendered outside this wrapper picks up the same light/dark overrides.
    useEffect(() => {
        document.body.classList.add("theme-app");
        document.body.setAttribute("data-theme", theme);
        return () => {
            document.body.classList.remove("theme-app");
            document.body.removeAttribute("data-theme");
        };
    }, [theme]);
    return (
        <div data-theme={theme} className={`theme-app ${className}`}>
            {children}
        </div>
    );
};
